import React, { useEffect, useState } from "react";
import { Database, RefreshCw, Key, ChevronLeft } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import baseapi from "../../../services/baseapi";
import DBCredentialModal from "./DBCredentialModal";
import { showError } from "../../../utils/toast";

interface DBMonitorProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

const AssetDBMonitor: React.FC<DBMonitorProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [dbInstances, setDbInstances] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const [urlInstanceId = null, urlDbType = null] = [searchParams.get("instanceId"), searchParams.get("dbType")];

    const [activeTab, setActiveTab] = useState<"overview" | "list">("overview");
    const [overviewDashboardUrl, setOverviewDashboardUrl] = useState<string | null>(null);
    const [isOverviewLoading, setIsOverviewLoading] = useState<boolean>(false);

    const [instanceDashboardUrl, setInstanceDashboardUrl] = useState<string | null>(null);
    const [isInstanceDashboardLoading, setIsInstanceDashboardLoading] = useState<boolean>(false);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedInstance, setSelectedInstance] = useState<any>(null);
    const getDashboardNameForDbType = (dbType: string) => {
        const type = dbType?.toLowerCase();
        if (type === "postgresql" || type === "postgres") return "PostgreSQL Monitoring";
        if (type === "mongodb" || type === "mongo") return "MongoDB Monitoring";
        if (type === "redis") return "Redis Monitoring";
        if (type === "elasticsearch" || type === "elastic") return "Elasticsearch Monitoring";
        return null;
    };

    useEffect(() => {
        const fetchDBInstances = async () => {
            setLoading(true);
            try {
                const queryObj: any = {};
                if (asset_id) queryObj.asset_id = asset_id;
                if (agent_id) queryObj.agent_id = agent_id;

                const params = {
                    client_id,
                    organization_id,
                    query: JSON.stringify(queryObj),
                };
                const response = await baseapi.get("/asset_db_instances", { params });
                if (response.data.status && response.data.data) {
                    setDbInstances(response.data.data);
                } else {
                    setDbInstances([]);
                }
            } catch (e: any) {
                showError(e.message || "Failed to load DB instances");
                setDbInstances([]);
            } finally {
                setLoading(false);
            }
        };

        if (asset_id) {
            fetchDBInstances();
        }
    }, [agent_id, asset_id, organization_id, client_id, refreshKey]);
    useEffect(() => {
        if (activeTab === "overview") {
            const fetchOverviewDashboard = async () => {
                setIsOverviewLoading(true);
                try {
                    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
                    const currentClientId = client_id || info.client_id;
                    const reqParams: any = {
                        dashboard: "DB Monitoring Overview",
                        client_id: currentClientId,
                        asset_id: asset_id,
                        agent_id: agent_id
                    };
                    const response = await baseapi.get("/dashboards/embed-url", { params: reqParams });
                    if (response.data.status) {
                        let url = response.data.iframe_url || response.data.url || response.data.embed_url;
                        if (url) {
                            if (url.startsWith('/')) {
                                url = window.location.origin + url;
                            }
                            setOverviewDashboardUrl(url);
                        }
                    } else {
                        showError(response.data.message || "Failed to load overview dashboard embed URL");
                    }
                } catch (error: any) {
                    showError(error.message || "Failed to fetch overview dashboard URL");
                } finally {
                    setIsOverviewLoading(false);
                }
            };
            fetchOverviewDashboard();
        }
    }, [activeTab, asset_id, agent_id, client_id]);

    useEffect(() => {
        if (urlInstanceId && urlDbType) {
            const fetchInstanceDashboard = async () => {
                setIsInstanceDashboardLoading(true);
                try {
                    const dashboardName = getDashboardNameForDbType(urlDbType);
                    if (!dashboardName) {
                        showError(`No dashboard configured for DB type: ${urlDbType}`);
                        setIsInstanceDashboardLoading(false);
                        return;
                    }

                    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
                    const currentClientId = client_id || info.client_id;

                    const reqParams: any = {
                        dashboard: dashboardName,
                        client_id: currentClientId,
                        asset_id: asset_id,
                        agent_id: agent_id,
                        filters: JSON.stringify({ "instance_id.keyword": urlInstanceId })
                    };

                    const response = await baseapi.get("/dashboards/embed-url", { params: reqParams });
                    if (response.data.status) {
                        let url = response.data.iframe_url || response.data.url || response.data.embed_url;
                        if (url) {
                            if (url.startsWith('/')) {
                                url = window.location.origin + url;
                            }
                            setInstanceDashboardUrl(url);
                        }
                    } else {
                        showError(response.data.message || "Failed to load instance dashboard embed URL");
                    }
                } catch (error: any) {
                    showError(error.message || "Failed to fetch instance dashboard URL");
                } finally {
                    setIsInstanceDashboardLoading(false);
                }
            };
            fetchInstanceDashboard();
        } else {
            setInstanceDashboardUrl(null);
        }
    }, [urlInstanceId, urlDbType, asset_id, agent_id, client_id]);

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };
    if (urlInstanceId) {
        return (
            <div className="p-3 bg-gray-900 w-full min-h-[calc(100vh-100px)] rounded-xl flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <button
                        onClick={() => {
                            searchParams.delete("instanceId");
                            searchParams.delete("dbType");
                            setSearchParams(searchParams);
                        }}
                        className="px-4 py-2 bg-gray-800 text-gray-200 rounded hover:bg-gray-700 transition-colors flex items-center gap-2 text-sm font-bold tracking-wider"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back to Instances
                    </button>
                    <h2 className="text-gray-200 font-bold uppercase tracking-wider">{urlDbType} Dashboard</h2>
                </div>

                <div className="flex-1 min-h-[700px] w-full bg-gray-900 rounded-md border border-gray-700 relative flex items-center justify-center overflow-hidden">
                    {isInstanceDashboardLoading ? (
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-gray-300 text-xs font-bold uppercase tracking-wider">Loading dashboard...</span>
                        </div>
                    ) : instanceDashboardUrl ? (
                        <iframe src={instanceDashboardUrl} className="w-full h-full border-none absolute inset-0 bg-transparent" title="Instance Dashboard" />
                    ) : (
                        <div className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">Failed to load dashboard</div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="p-3 bg-gray-900 w-full min-h-[calc(100vh-100px)] rounded-xl flex flex-col">
            <div className="flex justify-between items-center border-b border-gray-700 mb-4 shrink-0">
                <div className="flex">
                    <button
                        onClick={() => setActiveTab("overview")}
                        className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 ${activeTab === "overview" ? "border-orange-500 text-orange-500" : "border-transparent text-gray-400 hover:text-gray-200"}`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab("list")}
                        className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 ${activeTab === "list" ? "border-orange-500 text-orange-500" : "border-transparent text-gray-400 hover:text-gray-200"}`}
                    >
                        DB Instances
                    </button>
                </div>

                {activeTab === "list" && (
                    <div className="flex items-center gap-4">
                        <div className="flex items-center">
                            <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                                <Database className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex flex-col">
                                <h2 className="text-sm font-bold text-white leading-tight">DB Monitor</h2>
                                <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                                    {dbInstances.length} Instances
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                            title="Refresh data"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                        </button>
                    </div>
                )}
            </div>

            {activeTab === "list" ? (
                <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden flex flex-col relative min-h-[300px]">
                    <div className="flex-1 overflow-x-auto overflow-y-auto custom-scroll">
                        <table className="w-full min-w-max text-left border-collapse">
                            <thead className="bg-gray-800/80 sticky top-0 z-10">
                                <tr>
                                    <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700">Instance ID</th>
                                    <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700">DB Type</th>
                                    <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700">Host</th>
                                    <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700">Port</th>
                                    <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700">Process Name</th>
                                    <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700">Version</th>
                                    <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700">Status</th>
                                    <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700">Monitoring Mode</th>
                                    <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700">Platform</th>
                                    <th className="px-3 py-2 text-center text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {loading ? (
                                    <tr>
                                        <td colSpan={10} className="h-48 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-3">
                                                <div className="w-6 h-6 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : dbInstances.length === 0 ? (
                                    <tr>
                                        <td colSpan={10} className="h-48 text-center text-gray-500 text-[11px] font-bold uppercase tracking-widest">
                                            No Database instances found
                                        </td>
                                    </tr>
                                ) : (
                                    dbInstances.map((db, idx) => (
                                        <tr key={idx} className="hover:bg-gray-800/40 transition-colors duration-100">
                                            <td className="px-3 py-2 whitespace-nowrap text-[11px] font-semibold text-white">{db.instance_id || '-'}</td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-tighter bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                                    {db.db_type || '-'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-300">{db.host || '-'}</td>
                                            <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-300 font-mono">{db.port || '-'}</td>
                                            <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-300">{db.process_name || '-'}</td>
                                            <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-300 font-mono">{db.version || '-'}</td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-tighter ${db.status === 'running' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'
                                                    }`}>
                                                    {db.status || '-'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-300 uppercase tracking-tighter">{db.monitoring_mode || '-'}</td>
                                            <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-300 uppercase tracking-tighter">{db.platform || '-'}</td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => {
                                                            searchParams.set("instanceId", db.instance_id);
                                                            searchParams.set("dbType", db.db_type);
                                                            setSearchParams(searchParams);
                                                        }}
                                                        className="px-2 py-1 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border border-orange-500/20 rounded text-[10px] font-bold uppercase tracking-tighter transition-colors"
                                                    >
                                                        View stats
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setSelectedInstance(db);
                                                            setIsModalOpen(true);
                                                        }}
                                                        className="px-2 py-1 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border border-orange-500/20 rounded text-[10px] font-bold uppercase tracking-tighter flex items-center gap-1 transition-colors"
                                                    >
                                                        <Key className="w-3 h-3" />
                                                        Add Credential
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="flex-1 min-h-[700px] w-full bg-gray-900 rounded-md border border-gray-700 relative flex items-center justify-center overflow-hidden">
                    {isOverviewLoading ? (
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-gray-300 text-xs font-bold uppercase tracking-wider">Loading overview...</span>
                        </div>
                    ) : overviewDashboardUrl ? (
                        <iframe src={overviewDashboardUrl} className="w-full h-full border-none absolute inset-0 bg-transparent" title="DB Overview Dashboard" />
                    ) : (
                        <div className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">Failed to load overview</div>
                    )}
                </div>
            )}

            {selectedInstance && (
                <DBCredentialModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        setSelectedInstance(null);
                    }}
                    dbType={selectedInstance.db_type}
                    agent_id={agent_id}
                    asset_id={asset_id}
                    instance_id={selectedInstance.instance_id}
                    port={selectedInstance.port}
                />
            )}
        </div>
    );
};

export default AssetDBMonitor;
